import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  LogOut,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Logo } from "../components/Logo";
import { ApiError, createWorkspace, lookupWorkspace, requestAccess } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/cn";

function slugify(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

/** First name for the greeting; ignores IdP placeholders like "unknown". */
function greetingName(fullName: string | null | undefined, email: string | undefined): string {
  const placeholder = /^(unknown|unknown unknown|n\/a|none|null|-)$/i;
  const name = (fullName ?? "").trim();
  if (name && !placeholder.test(name)) return name.split(/\s+/)[0];
  // Fall back to a tidy version of the email's local part, if it looks like a name.
  const local = (email ?? "").split("@")[0].replace(/[._-]+/g, " ").trim();
  const first = local.split(/\s+/)[0];
  if (first && first.length >= 2 && first.length <= 14 && /^[a-z]+$/i.test(first)) {
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  return "";
}

type Mode = "choose" | "create" | "join";

export default function Onboarding() {
  const { user, pendingRequests, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("choose");

  // create
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const effectiveSlug = slugEdited ? slug : slugify(name);

  // join
  const [joinSlug, setJoinSlug] = useState("");
  const [found, setFound] = useState<{ slug: string; display_name: string } | null>(null);
  const [message, setMessage] = useState("");
  const [requested, setRequested] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createWorkspace(effectiveSlug, name);
      await refresh();
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the workspace");
      setBusy(false);
    }
  }

  async function onFind(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFound(null);
    try {
      setFound(await lookupWorkspace(slugify(joinSlug)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server");
    } finally {
      setBusy(false);
    }
  }

  async function onRequest() {
    if (!found) return;
    setBusy(true);
    setError(null);
    try {
      await requestAccess(found.slug, message);
      setRequested(found.slug);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send the request");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 py-16">
      <div className="pointer-events-none absolute inset-0 bg-aurora opacity-60" />
      <div className="pointer-events-none absolute -left-24 -top-16 h-72 w-72 rounded-full bg-accent-600/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />

      <div className="relative mx-auto max-w-2xl px-6">
        <div className="flex items-center justify-between">
          <Logo light size={40} />
          <button
            onClick={logout}
            className="focus-ring inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-slate-400 transition hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="mt-10 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {(() => {
              const first = greetingName(user?.full_name, user?.email);
              return first ? `Welcome, ${first}.` : "Welcome.";
            })()}
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-slate-300">
            You're signed in, but you don't belong to a workspace yet. Create one, or ask to join an
            existing workspace.
          </p>
          {user && <p className="mt-2 text-sm text-slate-500">{user.email}</p>}
        </div>

        {/* Already-pending requests */}
        {pendingRequests.length > 0 && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              <Clock className="h-4 w-4 text-amber-300" /> Awaiting approval
            </p>
            <ul className="mt-3 space-y-2">
              {pendingRequests.map((p) => (
                <li key={p.tenant_slug} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">
                    {p.tenant_name} <span className="font-mono text-xs text-slate-500">({p.tenant_slug})</span>
                  </span>
                  <Badge tone="amber">Pending</Badge>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              An owner or admin of that workspace needs to approve you. You can refresh this page once
              they do.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200 ring-1 ring-inset ring-rose-500/30">
            {error}
          </div>
        )}

        {/* Choice */}
        {mode === "choose" && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              {
                key: "create" as const,
                icon: Plus,
                title: "Create a workspace",
                body: "Start fresh — you'll be the owner and can invite your team.",
              },
              {
                key: "join" as const,
                icon: Users,
                title: "Request access",
                body: "Join a workspace your team already uses. An admin approves you.",
              },
            ].map((c) => (
              <button
                key={c.key}
                onClick={() => {
                  setMode(c.key);
                  setError(null);
                }}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left backdrop-blur transition hover:border-white/25 hover:bg-white/[0.06]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-glow">
                  <c.icon className="h-5 w-5" />
                </span>
                <p className="mt-4 font-semibold text-white">{c.title}</p>
                <p className="mt-1 text-sm text-slate-400">{c.body}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-300">
                  Continue <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Create */}
        {mode === "create" && (
          <form
            onSubmit={onCreate}
            className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur sm:p-7"
          >
            <p className="flex items-center gap-2 text-base font-semibold text-white">
              <Building2 className="h-5 w-5 text-accent-300" /> Create your workspace
            </p>
            <div className="mt-5 space-y-4 [&_label]:text-slate-300">
              <Field label="Workspace name">
                <Input
                  required
                  autoFocus
                  placeholder="Acme Corp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label="Workspace URL" hint="Lower-case letters, numbers and dashes.">
                <Input
                  required
                  pattern="[a-z0-9][a-z0-9\-]*"
                  placeholder="acme"
                  value={effectiveSlug}
                  onChange={(e) => {
                    setSlugEdited(true);
                    setSlug(slugify(e.target.value));
                  }}
                />
              </Field>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="text-sm text-slate-400 hover:text-white"
              >
                ← Back
              </button>
              <Button type="submit" loading={busy} disabled={!name || !effectiveSlug}>
                Create workspace
              </Button>
            </div>
          </form>
        )}

        {/* Join */}
        {mode === "join" && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur sm:p-7">
            {requested ? (
              <div className="py-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <p className="mt-5 text-lg font-semibold text-white">Request sent</p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-400">
                  An owner or admin of <span className="font-mono text-slate-300">{requested}</span> will
                  review it. You'll get access as soon as they approve.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => {
                    setRequested(null);
                    setFound(null);
                    setJoinSlug("");
                    setMode("choose");
                  }}
                >
                  Done
                </Button>
              </div>
            ) : (
              <>
                <p className="flex items-center gap-2 text-base font-semibold text-white">
                  <Users className="h-5 w-5 text-accent-300" /> Request access to a workspace
                </p>
                <form onSubmit={onFind} className="mt-5 flex gap-3">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      required
                      autoFocus
                      className="pl-10"
                      placeholder="workspace-name"
                      value={joinSlug}
                      onChange={(e) => setJoinSlug(e.target.value)}
                    />
                  </div>
                  <Button type="submit" variant="outline" loading={busy}>
                    Find
                  </Button>
                </form>

                {found && (
                  <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-sm font-semibold text-white">{found.display_name}</p>
                    <p className="font-mono text-xs text-slate-400">{found.slug}</p>
                    <div className={cn("mt-4 space-y-3 [&_label]:text-slate-300")}>
                      <Field label="Message (optional)">
                        <Input
                          placeholder="I'm on the integrations team…"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                        />
                      </Field>
                    </div>
                    <Button className="mt-4 w-full" loading={busy} onClick={onRequest}>
                      Request access
                    </Button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setMode("choose")}
                  className="mt-6 text-sm text-slate-400 hover:text-white"
                >
                  ← Back
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
